import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Stack,
  Alert,
} from '@mui/material';
import { ADDABLE_MEMBER_RELATIONSHIPS } from '../../types/memberRelationships';
import { GENDER_OPTIONS } from '../../types/genderOptions';
import type { FamilyMember } from '../../types/user';
import { validateMemberForm, MEMBER_FORM_INITIAL, type MemberFormValues } from '../../utils/memberForm';
import { createMember, updateMember } from '../../services/membersApi';

interface MemberFormProps {
  mode: 'create' | 'edit';
  familyId: string;
  initialValue?: FamilyMember;
  onSuccess: () => void;
  onCancel: () => void;
}

function splitName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/);
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') };
}

/**
 * D8 — create/edit form for a family self-added permanent member.
 *
 * Create captures name, relationship, optional DOB / phone / email / dietary.
 * Edit shows only the fields represented on the App member row (name,
 * relationship, dietary) — phone / email / DOB live in the CRM and are not
 * prefilled, so they are hidden on edit to avoid clobbering them.
 */
const MemberForm: React.FC<MemberFormProps> = ({ mode, familyId, initialValue, onSuccess, onCancel }) => {
  const init: MemberFormValues = initialValue
    ? {
        ...MEMBER_FORM_INITIAL,
        ...splitName(initialValue.name),
        relationship: initialValue.relationship as string,
        dietaryRequirements: initialValue.dietaryRequirements ?? '',
      }
    : MEMBER_FORM_INITIAL;

  const [values, setValues] = useState<MemberFormValues>(init);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof MemberFormValues) => (
    e: React.ChangeEvent<HTMLInputElement | { value: unknown }>,
  ) => setValues((v) => ({ ...v, [field]: (e.target as { value: string }).value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;

    const result = validateMemberForm(values, mode);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'create') {
        await createMember(familyId, result.body);
      } else if (initialValue) {
        // Edit sends only the App-row fields; dietary is explicit so it can be
        // cleared. Phone/email/DOB are not shown on edit (CRM-only).
        await updateMember(familyId, initialValue.id, {
          firstName: result.body.firstName,
          lastName: result.body.lastName,
          relationship: result.body.relationship,
          dietaryRequirements: values.dietaryRequirements.trim() || null,
          ...(values.gender ? { gender: values.gender } : {}),
        });
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save family member');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="First name"
          value={values.firstName}
          onChange={set('firstName')}
          required
          autoFocus
          error={!!errors.firstName}
          helperText={errors.firstName}
          inputProps={{ maxLength: 100 }}
        />
        <TextField
          label="Last name"
          value={values.lastName}
          onChange={set('lastName')}
          required
          error={!!errors.lastName}
          helperText={errors.lastName}
          inputProps={{ maxLength: 100 }}
        />

        <FormControl required error={!!errors.relationship}>
          <InputLabel id="member-relationship-label">Relationship</InputLabel>
          <Select
            labelId="member-relationship-label"
            label="Relationship"
            value={values.relationship}
            onChange={(e) => setValues((v) => ({ ...v, relationship: e.target.value as string }))}
          >
            {ADDABLE_MEMBER_RELATIONSHIPS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
          {errors.relationship && <FormHelperText>{errors.relationship}</FormHelperText>}
        </FormControl>

        <FormControl required={mode === 'create'} error={!!errors.gender}>
          <InputLabel id="member-gender-label">Gender</InputLabel>
          <Select
            labelId="member-gender-label"
            label="Gender"
            value={values.gender}
            onChange={(e) => setValues((v) => ({ ...v, gender: e.target.value as string }))}
          >
            {GENDER_OPTIONS.map((g) => (
              <MenuItem key={g.value} value={g.value}>
                {g.label}
              </MenuItem>
            ))}
          </Select>
          {errors.gender ? (
            <FormHelperText>{errors.gender}</FormHelperText>
          ) : mode === 'edit' ? (
            <FormHelperText>Leave blank to keep the current gender.</FormHelperText>
          ) : null}
        </FormControl>

        {mode === 'create' && (
          <>
            <TextField
              label="Date of birth (optional)"
              type="date"
              value={values.dob}
              onChange={set('dob')}
              error={!!errors.dob}
              helperText={errors.dob}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Phone (optional)"
              value={values.phone}
              onChange={set('phone')}
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Email (optional)"
              type="email"
              value={values.email}
              onChange={set('email')}
              error={!!errors.email}
              helperText={errors.email}
              inputProps={{ maxLength: 200 }}
            />
          </>
        )}

        <TextField
          label="Dietary requirements (optional)"
          placeholder="e.g. nut allergy, gluten free"
          value={values.dietaryRequirements}
          onChange={set('dietaryRequirements')}
          inputProps={{ maxLength: 500 }}
        />

        <FormHelperText>
          You can add grandparents, step-parents, carers, siblings and other family members. New
          members don't get their own app login — contact Team Jak if they need one.
        </FormHelperText>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
          <Button onClick={onCancel} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {mode === 'create' ? 'Add family member' : 'Save changes'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default MemberForm;
